import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Grid, Paper } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import { useToast } from './ui/ToastProvider';
import { DEMO_TRANSACTIONS } from '../utils/demoTransactions';
import { transactionStore, PendingAmendment } from '../utils/transactionStore';
// Derive pending amendments from DEMO_TRANSACTIONS that have amendStatus: 'PENDING'
const deriveDemoAmendments = (): PendingAmendment[] => {
  return DEMO_TRANSACTIONS.filter(txn => txn.amendStatus === 'PENDING' && txn.amendmentHistory && txn.amendmentHistory.length > 0).map((txn, index) => {
    const latestAmendment = txn.amendmentHistory![txn.amendmentHistory!.length - 1];
    const reasons = ['Beneficiary provided updated account number', 'Typo in original account number', 'Account was dormant, new active account provided'];
    return {
      id: `AMEND-DEMO-${txn.id}`,
      transactionId: txn.id,
      mtoName: txn.mtoName,
      payoutType: txn.payoutType,
      amountFCY: txn.amountFCY,
      currencyFCY: txn.currencyFCY,
      currentState: txn.currentState,
      amendmentReason: reasons[index % reasons.length],
      requestedBy: latestAmendment.requestedBy,
      requestedAt: latestAmendment.timestamp,
      status: 'PENDING' as const,
      originalAccount: latestAmendment.oldAccount,
      newAccount: latestAmendment.newAccount,
      fetchedTitle: latestAmendment.fetchedTitle
    };
  });
};
const CheckerAmendmentTable: React.FC = () => {
  const [amendments, setAmendments] = useState<PendingAmendment[]>([]);
  const [selectedAmendment, setSelectedAmendment] = useState<PendingAmendment | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const {
    showToast
  } = useToast();
  // Combine demo amendments + dynamically submitted amendments from store
  const refreshAmendments = () => {
    const demoAmendments = deriveDemoAmendments();
    const storeAmendments = transactionStore.getPendingAmendments();
    // Merge: demo amendments that haven't been acted on + store amendments
    setAmendments([...demoAmendments, ...storeAmendments]);
  };
  useEffect(() => {
    refreshAmendments();
    const unsubscribe = transactionStore.subscribe(refreshAmendments);
    return unsubscribe;
  }, []);
  const handleAction = (amendment: PendingAmendment, action: 'approve' | 'reject') => {
    setSelectedAmendment(amendment);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selectedAmendment || !actionType) return;
    if (!reason.trim()) {
      return;
    }
    setIsLoading(true);
    try {
      // For demo amendments (AMEND-DEMO-*), register them in the store first
      // so the transaction override tracking works
      if (selectedAmendment.id.startsWith('AMEND-DEMO-')) {
        transactionStore.registerDemoAmendment(selectedAmendment);
      }
      // Update in the transaction store
      if (actionType === 'approve') {
        transactionStore.approveAmendment(selectedAmendment.id, reason);
      } else {
        transactionStore.rejectAmendment(selectedAmendment.id, reason);
      }
      // Remove from local pending list
      setAmendments(prev => prev.filter(a => a.id !== selectedAmendment.id));
      showToast(`Amendment request ${actionType === 'approve' ? 'approved' : 'rejected'} for ${selectedAmendment.transactionId}`, actionType === 'approve' ? 'success' : 'info');
      setActionDialogOpen(false);
      setReason('');
      setSelectedAmendment(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const handleViewDetails = (amendment: PendingAmendment) => {
    setSelectedAmendment(amendment);
    setDetailsDrawerOpen(true);
  };
  const rowActions: RowAction<PendingAmendment>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => handleViewDetails(row)
  }, {
    icon: 'lucide:check-circle',
    label: 'Approve',
    onClick: row => handleAction(row, 'approve')
  }, {
    icon: 'lucide:x-circle',
    label: 'Reject',
    onClick: row => handleAction(row, 'reject')
  }];
  const columns: Column<PendingAmendment>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-458">
          {row.id}
        </Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-459">
          {row.transactionId}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-460">
          {row.mtoName}
        </Typography>
  }, {
    id: 'originalAccount',
    label: 'Original Account',
    renderCell: row => <Typography variant="body2" fontFamily="monospace" sx={{
      textDecoration: 'line-through',
      color: 'text.secondary'
    }} noWrap data-id="element-461">
          {row.originalAccount.substring(0, 12)}...
        </Typography>
  }, {
    id: 'newAccount',
    label: 'New Account',
    renderCell: row => <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="primary.main" noWrap data-id="element-462">
          {row.newAccount.substring(0, 12)}...
        </Typography>
  }, {
    id: 'fetchedTitle',
    label: 'Fetched Title',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-463">
          {row.fetchedTitle}
        </Typography>
  }, {
    id: 'requestedBy',
    label: 'Requested By',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-464">
          {row.requestedBy}
        </Typography>
  }, {
    id: 'requestedAt',
    label: 'Requested At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-465">
          {new Date(row.requestedAt).toLocaleString()}
        </Typography>
  }];
  return <>
      <DataTable title="Pending Amendment Requests" data={amendments} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No pending amendment requests" initialVisibleColumns={['transactionId', 'mtoName', 'originalAccount', 'newAccount', 'fetchedTitle', 'requestedBy', 'requestedAt']} onRowClick={handleViewDetails} data-id="element-466" />

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-467">
        <DialogTitle data-id="element-468">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-469">
            <Icon icon={actionType === 'approve' ? 'lucide:check-circle' : 'lucide:x-circle'} width={24} color={actionType === 'approve' ? '#10B981' : '#EF4444'} data-id="element-470" />
            <Typography variant="h6" fontWeight={600} data-id="element-471">
              {actionType === 'approve' ? 'Approve' : 'Reject'} Amendment
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-472">
          <Box sx={{
          mt: 1
        }} data-id="element-473">
            {selectedAmendment && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-474">
                  Transaction ID:{' '}
                  <strong data-id="element-475">{selectedAmendment.transactionId}</strong>
                </Typography>

                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-476">
                  <Grid container spacing={2} data-id="element-477">
                    <Grid item xs={12} sm={6} data-id="element-478">
                      <Typography variant="caption" color="text.secondary" data-id="element-479">
                        Original Account
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" sx={{
                    textDecoration: 'line-through',
                    color: 'text.secondary'
                  }} data-id="element-480">
                        {selectedAmendment.originalAccount}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} data-id="element-481">
                      <Typography variant="caption" color="text.secondary" data-id="element-482">
                        New Account
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="primary.main" data-id="element-483">
                        {selectedAmendment.newAccount}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} data-id="element-484">
                      <Typography variant="caption" color="text.secondary" data-id="element-485">
                        Fetched Title
                      </Typography>
                      <Typography variant="body2" fontWeight={600} data-id="element-486">
                        {selectedAmendment.fetchedTitle}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-487">
                  Maker's Reason:{' '}
                  <strong data-id="element-488">{selectedAmendment.amendmentReason}</strong>
                </Typography>
              </>}

            <TextField label={`Reason for ${actionType === 'approve' ? 'Approval' : 'Rejection'}`} placeholder={`Enter reason for ${actionType === 'approve' ? 'approving' : 'rejecting'} this amendment...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-489" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-490">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-491">
            Cancel
          </Button>
          <Button variant="contained" color={actionType === 'approve' ? 'success' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-493" /> : <Icon icon={actionType === 'approve' ? 'lucide:check' : 'lucide:x'} width={16} data-id="element-494" />} data-id="element-492">
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Drawer */}
      {selectedAmendment && <TransactionDetailsDrawer open={detailsDrawerOpen} onClose={() => {
      setDetailsDrawerOpen(false);
      setSelectedAmendment(null);
    }} transaction={{
      id: selectedAmendment.transactionId,
      mtoName: selectedAmendment.mtoName,
      mtoRef: `MTO-${selectedAmendment.transactionId}`,
      bankRef: `BNK-${selectedAmendment.transactionId}`,
      branchCode: '',
      agentCode: '',
      payoutType: selectedAmendment.payoutType,
      amountFCY: selectedAmendment.amountFCY,
      currencyFCY: selectedAmendment.currencyFCY,
      amountPKR: (parseFloat(selectedAmendment.amountFCY) * 15).toString(),
      corridor: 'GH-UK',
      purposeCode: 'FAM',
      purposeText: 'Personal Remittance',
      currentState: selectedAmendment.currentState as any,
      lifecycleStatus: 'FAILED' as any,
      createdAt: selectedAmendment.requestedAt,
      updatedAt: selectedAmendment.requestedAt,
      remitterName: 'Remitter Name',
      beneficiaryName: 'Beneficiary Name',
      beneficiaryIBAN: selectedAmendment.originalAccount,
      railRefs: {},
      stateHistory: []
    } as any} data-id="element-495" />}
    </>;
};
export default CheckerAmendmentTable;